import React, {useRef} from 'react';
import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {
    ConditionalRender,
    LegsOffset,
} from 'components/customer/Preview3D/types';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import roomPlannerAdjustableLegs from 'components/customer/RoomPlanner/hardware/useAdjustableLegs';
import {isFloatingVanityTemplate} from 'components/customer/RoomPlanner/helpers/renderer/panelTypeHelpers';

const useAdjustableLegs = (
    scene: React.MutableRefObject<THREE.Scene>,
    isLeftReturn = false,
    hasLShapedShelves = false,
    offset?: LegsOffset,
    cabinetGroupRef?: React.MutableRefObject<THREE.Group | null>,
    carcaseThickness = 0
) => {
    const adjustableLegModel =
        useRef<THREE.Group<THREE.Object3DEventMap | null>>(null);

    const {values, productDataStore} = use3DContext();

    const isFloatingVanity = isFloatingVanityTemplate(
        productDataStore?.current?.template_3d?.[0]?.attributes?.template
    );

    const transformAdjustableLegsModel = (
        type:
            | 'BOTTOM_LEFT'
            | 'TOP_RIGHT'
            | 'TOP_LEFT'
            | 'BOTTOM_RIGHT'
            | 'FRONT'
            | 'BACK',
        model: THREE.Group<THREE.Object3DEventMap>,
        yOffset = 0
    ): THREE.Group<THREE.Object3DEventMap> => {
        const degreesX = 90;
        const rotationX = (degreesX * Math.PI) / 180;
        const degreesY = 180;
        const rotationY = (degreesY * Math.PI) / 180;
        const legsZOffset = -109;
        const modelScale = 750;
        const includeFaces =
            typeof values.include_drawer_faces !== 'undefined'
                ? Boolean(values.include_drawer_faces)
                : true;
        const voidWidth =
            values.cabinet_void_width &&
            Boolean(values.cabinet_cover_void) &&
            includeFaces
                ? parseFloat(values.cabinet_void_width?.toString())
                : 0;

        const multiplier = isLeftReturn ? -1 : 1;
        const cabinetWidth = Number(values.cabinet_width);
        const widthForRightLegs = cabinetWidth - voidWidth * multiplier;
        const widthForLeftLegs = cabinetWidth + voidWidth * multiplier;
        const xPosition = (-(voidWidth / 2) - 33 * multiplier) * multiplier;

        model.scale.set(modelScale, modelScale, modelScale);
        model.position.z = -(values.cabinet_height / 2) + legsZOffset;
        model.rotation.x = rotationX;

        switch (type) {
            case 'TOP_RIGHT':
                model.rotation.y = rotationY;
                model.position.x = widthForRightLegs / 2;
                model.position.y = -(values.cabinet_depth / 2) + 100 + yOffset;
                break;
            case 'TOP_LEFT':
                model.position.x = -(widthForLeftLegs / 2);
                model.position.y = -(values.cabinet_depth / 2) + 35 + yOffset;
                break;
            case 'BOTTOM_RIGHT':
                model.rotation.y = rotationY;
                model.position.x = widthForRightLegs / 2;
                model.position.y = values.cabinet_depth / 2 - 35 - yOffset;
                break;
            case 'BACK':
                model.position.x = voidWidth !== 0 ? xPosition : -33;
                model.position.y = values.cabinet_depth / 2 - 110 - yOffset;
                break;
            case 'FRONT':
                model.position.x = voidWidth !== 0 ? xPosition : -33;
                model.position.y = -(values.cabinet_depth / 2) + 35 + yOffset;
                break;
            default:
                model.position.x = -(widthForLeftLegs / 2);
                model.position.y = values.cabinet_depth / 2 - 100 - yOffset;
                break;
        }

        return model;
    };

    const addAdjustableLegs = (model: THREE.Group<THREE.Object3DEventMap>) => {
        const conditionalWidth = 600;
        const cabinetWidth = Number(values.cabinet_width);

        if (values.cabinet_depth >= 250) {
            const bottomLeftModel = transformAdjustableLegsModel(
                'BOTTOM_LEFT',
                model.clone(),
                offset?.bottomLeft
            );
            scene.current.add(bottomLeftModel);
        }

        const topRightModel = transformAdjustableLegsModel(
            'TOP_RIGHT',
            model.clone(),
            offset?.topRight
        );
        scene.current.add(topRightModel);

        const topLeftModel = transformAdjustableLegsModel(
            'TOP_LEFT',
            model.clone(),
            offset?.topLeft
        );
        scene.current.add(topLeftModel);

        if (values.cabinet_depth >= 250) {
            const bottomRightModel = transformAdjustableLegsModel(
                'BOTTOM_RIGHT',
                model.clone(),
                offset?.bottomRight
            );
            scene.current.add(bottomRightModel);
        }

        if (cabinetWidth >= conditionalWidth) {
            const frontModel = transformAdjustableLegsModel(
                'FRONT',
                model.clone(),
                offset?.front
            );
            scene.current.add(frontModel);
        }

        if (
            cabinetWidth >= conditionalWidth * 2 &&
            values.cabinet_depth >= 250
        ) {
            const backModel = transformAdjustableLegsModel(
                'BACK',
                model.clone(),
                offset?.back
            );
            scene.current.add(backModel);
        }
    };

    const applyDynamicAdjustableLegs = () => {
        if (hasLShapedShelves) {
            const target = (cabinetGroupRef?.current ??
                scene.current) as THREE.Group;
            roomPlannerAdjustableLegs.applyDynamicAdjustableLegs(
                target,
                {...values, cabinet_carc_thickness: carcaseThickness},
                true,
                Number(values.cabinet_toekick)
            );
            return;
        }

        if (adjustableLegModel.current) {
            addAdjustableLegs(adjustableLegModel.current);
        } else {
            const loader = new GLTFLoader();

            loader.load(
                '/templates/3D/models/adjustable_leg.glb',
                function (gltf) {
                    const model = gltf.scene;
                    if (!Boolean(values.cabinet_include_hardware)) {
                        model.traverse(function (
                            child: THREE.Mesh & {
                                material: {
                                    transparent: boolean;
                                    opacity: number;
                                };
                            }
                        ) {
                            if (child.isMesh && child.material) {
                                child.material.transparent = true;
                                child.material.opacity = 0.1;
                            }
                        });
                    }

                    addAdjustableLegs(model);
                }
            );
        }
    };

    const adjustableLegsParts: ConditionalRender[] = [
        {
            condition:
                Boolean(Number(values.cabinet_adjustable_legs)) &&
                !isFloatingVanity,
            callback: applyDynamicAdjustableLegs,
        },
    ];

    return {
        adjustableLegsParts,
    };
};

export default useAdjustableLegs;
